import type { IContinuationResult, IAccount, IAccountInput, IAccountTenantInput, ITenantWithAccountTenant, IAccountLogin } from '../../../api/api'
import { omit } from '../util'
import { apiSlice } from './apiSlice'

export const adminAccountsApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAdminAccounts: builder.query<IAccount[], void>({ // eslint-disable-line @typescript-eslint/no-invalid-void-type
      query: () => '/admin/accounts',
      transformResponse: (responseData: IContinuationResult<IAccount>) => {
        // return responseData?.items ? responseData?.items.map(unmarshalList) : []
        return responseData?.items ?? []
      },

      providesTags: (result = [], _error, _arg) => [
        'AdminAccount',
        ...result.map(({ id }) => ({ type: 'AdminAccount' as const, id }))
      ]
    }),
    getAdminAccount: builder.query<IAccount | undefined, string>({
      query: (accountId: string) => `/admin/accounts/${accountId}`,
      // transformResponse: (responseData: List) => {
      //   return responseData ? unmarshalList(responseData) : undefined
      // },
      providesTags: (_result, _error, arg) => [{ type: 'AdminAccount', id: arg }]
    }),
    addAdminAccount: builder.mutation<IAccount, IAccountInput>({
      query: (newAccount: IAccountInput) => ({
        url: '/admin/accounts',
        method: 'POST',
        body: newAccount
      }),
      invalidatesTags: (result, _error, _arg) => ['AdminAccount', { type: 'AdminAccount', id: result?.id }]
    }),
    editAdminAccount: builder.mutation({
      query: (account: Partial<IAccountInput> & { id: string }) => ({
        url: `/admin/accounts/${account.id}`,
        method: 'PUT',
        body: account
      }),
      invalidatesTags: (result, _error, _arg) => [{ type: 'AdminAccount', id: result?.id }]
    }),
    generateAdminAccountCode: builder.mutation<{ code: string }, { accountId: string }>({
      query: (id: { accountId: string }) => ({
        url: `/admin/accounts/${id.accountId}/code`,
        method: 'POST'
      })
    }),
    deleteAdminAccount: builder.mutation({
      query: (id: { accountId: string }) => ({
        url: `/admin/accounts/${id.accountId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, arg) => ['AdminAccount', { type: 'AdminAccount', id: arg.accountId }]
    }),
    getAdminAccountTenants: builder.query<ITenantWithAccountTenant[], string>({
      query: (accountId: string) => `/admin/accounts/${accountId}/tenants`,
      // transformResponse: (responseData: List) => {
      //   return responseData ? unmarshalList(responseData) : undefined
      // },
      providesTags: (result = [], _error, _arg) => [
        'AdminAccountTenant',
        ...result.map(({ accountTenant }) => ({ type: 'AdminAccountTenant' as const, id: `${accountTenant?.accountId}#${accountTenant?.tenantId}` }))
      ]
    }),
    getAdminAccountLogins: builder.query<IAccountLogin[], string>({
      query: (accountId: string) => `/admin/accounts/${accountId}/logins`
    }),
    addAdminAccountTenant: builder.mutation<ITenantWithAccountTenant, IAccountTenantInput>({
      query: (input: IAccountTenantInput) => ({
        url: `/admin/accounts/${input.accountId}/tenants/${input.tenantId}`,
        method: 'POST',
        body: omit(input, 'tenantId', 'accountId')
      }),
      invalidatesTags: (_result, _error, arg) => ['AdminAccountTenant', { type: 'AdminAccountTenant', id: `${arg.accountId}#${arg.tenantId}` }]
    }),
    editAdminAccountTenant: builder.mutation<ITenantWithAccountTenant, IAccountTenantInput>({
      query: (input: IAccountTenantInput) => ({
        url: `/admin/accounts/${input.accountId}/tenants/${input.tenantId}`,
        method: 'PUT',
        body: omit(input, 'tenantId', 'accountId')
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'AdminAccountTenant', id: `${arg.accountId}#${arg.tenantId}` }]
    }),
    deleteAdminAccountTenant: builder.mutation({
      query: (id: { tenantId: string, accountId: string }) => ({
        url: `/admin/accounts/${id.accountId}/tenants/${id.tenantId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, arg) => ['AdminAccountTenant', { type: 'AdminAccountTenant', id: `${arg.accountId}#${arg.tenantId}` }]
    })
  })
})

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  useGetAdminAccountsQuery,
  useGetAdminAccountQuery,
  useAddAdminAccountMutation,
  useEditAdminAccountMutation,
  useGenerateAdminAccountCodeMutation,
  useDeleteAdminAccountMutation,
  useGetAdminAccountTenantsQuery,
  useGetAdminAccountLoginsQuery,
  useAddAdminAccountTenantMutation,
  useEditAdminAccountTenantMutation,
  useDeleteAdminAccountTenantMutation
} = adminAccountsApiSlice
